/**
 * Typeahead selector (F3), shared by BOTH adapters (plan §6.6 search contract).
 *
 * The ordering rule is the plan's, not the demo's: ticker-prefix matches first,
 * then name-substring matches, ticker ascending within each group, capped AFTER
 * ordering. The demo returns universe order, which is stable but arbitrary —
 * typing "A" there surfaces whatever happens to sit near the top of the RAW
 * string. Ordering here rather than per adapter is what makes the contract
 * provable by shared code instead of only by parity fixtures.
 */
import type { Ticker } from '@meridian/contracts';

import { COLLATOR } from '../constants/collation';

export interface SearchableRow {
  readonly ticker: Ticker;
  readonly name: string;
}

/** Product default; the API route's own `limit` param overrides it. */
export const SEARCH_LIMIT_DEFAULT = 6;

export function searchSecurities<T extends SearchableRow>(
  rows: readonly T[],
  query: string,
  limit: number = SEARCH_LIMIT_DEFAULT,
): readonly T[] {
  const q = query.trim().toUpperCase();
  if (q === '' || limit <= 0) return [];
  const byTicker = (a: T, b: T): number => COLLATOR.compare(a.ticker, b.ticker);
  const prefix = rows.filter((r) => r.ticker.toUpperCase().startsWith(q)).sort(byTicker);
  const matched = new Set(prefix.map((r) => r.ticker));
  const substring = rows
    .filter((r) => !matched.has(r.ticker) && r.name.toUpperCase().includes(q))
    .sort(byTicker);
  return [...prefix, ...substring].slice(0, limit);
}
